import React from 'react';

/**
 * Configuration for a marker pair that defines how to parse and render marked content
 */
export interface MarkerConfig {
  /** Start marker (invisible Unicode character) */
  start: string;
  /** End marker (invisible Unicode character) */
  end: string;
  /**
   * Factory function to create React element from marked content
   * @param content - The text content between the markers
   * @param index - Unique index for React key generation
   * @returns React element (e.g., <strong>, <span>, etc.)
   */
  createElement: (content: string, index: number) => React.ReactNode;
}

/**
 * Parses a string containing invisible Unicode markers and converts them to React elements
 *
 * This utility function enables ICU-compliant formatting while preserving custom styling.
 * It processes multiple marker types in a single pass, converting each marked section
 * to its corresponding React element while preserving surrounding plain text.
 *
 * HOW IT WORKS:
 * =============
 * 1. Finds all marker pairs in the text using regex
 * 2. Sorts matches by position to process in order
 * 3. Converts each marked section using the provided element factory
 * 4. Preserves plain text between markers
 * 5. Returns a React Fragment containing all parts
 *
 * EXAMPLE USAGE:
 * ==============
 * ```typescript
 * // Single marker type (bold)
 * const text = `Hello ${BOLD_START}world${BOLD_END}!`;
 * const result = parseMarkersToJSX(text, [{
 *   start: BOLD_START,
 *   end: BOLD_END,
 *   createElement: (content, index) =>
 *     React.createElement('strong', { key: index }, content)
 * }]);
 * // Result: React Fragment with "Hello ", <strong>world</strong>, "!"
 *
 * // Multiple marker types (field, operation, value)
 * const text = `${FIELD_START}city${FIELD_END} = ${VALUE_START}NYC${VALUE_END}`;
 * const result = parseMarkersToJSX(text, [
 *   { start: FIELD_START, end: FIELD_END, createElement: (c, i) => <span className="field">{c}</span> },
 *   { start: VALUE_START, end: VALUE_END, createElement: (c, i) => <span className="value">{c}</span> }
 * ]);
 * ```
 *
 * @param text - String containing marker pairs for different styles
 * @param markerConfigs - Array of marker configurations, each defining a marker pair and its React element factory
 * @returns ReactNode with markers converted to React elements, wrapped in a Fragment
 */
export function parseMarkersToJSX(
  text: string,
  markerConfigs: MarkerConfig[]
): React.ReactNode {
  const parts: React.ReactNode[] = [];
  let currentIndex = 0;
  let partIndex = 0;

  // Collect all matches with their positions and types
  const matches: {
    index: number;
    length: number;
    content: string;
    createElement: (content: string, index: number) => React.ReactNode;
  }[] = [];

  // Process each marker configuration
  markerConfigs.forEach((config) => {
    // Create regex to find marker pairs
    // Escapes special regex characters in markers
    const escapedStart = config.start.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const escapedEnd = config.end.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(
      `${escapedStart}([^${escapedEnd}]+)${escapedEnd}`,
      'g'
    );

    // Find all matches for this marker type
    const regexMatches = Array.from(text.matchAll(regex));

    regexMatches.forEach((match) => {
      matches.push({
        index: match.index,
        length: match[0].length,
        content: match[1],
        createElement: config.createElement,
      });
    });
  });

  // Sort matches by position to process in order
  matches.sort((a, b) => a.index - b.index);

  // Process matches in order
  matches.forEach((match) => {
    // Add plain text before this match
    if (match.index > currentIndex) {
      parts.push(text.slice(currentIndex, match.index));
    }

    // Add the marked text wrapped in appropriate React element
    parts.push(match.createElement(match.content, partIndex));
    partIndex += 1;

    // Update position for next iteration
    currentIndex = match.index + match.length;
  });

  // Add any remaining plain text after last match
  if (currentIndex < text.length) {
    parts.push(text.slice(currentIndex));
  }

  // Wrap in fragment to return as single ReactNode
  return React.createElement(React.Fragment, null, parts);
}
